/**
 * What a task needs from you, as one state dot and one plain line, shared
 * by board cards, list rows and the task pocket (spec §6.1, §6.2, §6.5),
 * plus the plain words of a task's second line. Text is translated when it
 * is asked for, so callers must re-render on a language change.
 */
import type { Task, TaskDependency } from "@/api/tasks";
import { translate } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import type { StatusTone } from "@/components/ui";
import type { AwaitingInput } from "@/stores/canvas-store";
import type { ActiveExecution } from "@/stores/companion-store";

export type TaskStateTone = Extract<
  StatusTone,
  "question" | "error" | "working"
>;

/** Board-wide sets behind the state dots (see `useTaskSignals`). */
export interface TaskSignals {
  questionTaskIds: ReadonlySet<string>;
  runningTaskIds: ReadonlySet<string>;
}

const LIVE_STATUSES = new Set<ActiveExecution["status"]>([
  "running",
  "waiting_approval",
]);

/** Blockers that still hold the task; none once the task is done. */
export function activeBlockers(
  task: Pick<Task, "status" | "blocked_by_detail">,
): TaskDependency[] {
  if (task.status === "done") return [];
  return task.blocked_by_detail.filter(
    (dependency) => dependency.status !== "done",
  );
}

/** A question wins over an error, and an error over work in progress. */
export function taskStateTone(
  task: Task,
  signals: { question: boolean; running: boolean },
): TaskStateTone | null {
  if (signals.question || task.needs_attention) return "question";
  if (
    task.status === "blocked" ||
    activeBlockers(task).length > 0 ||
    task.checks_status === "failure"
  ) {
    return "error";
  }
  return signals.running ? "working" : null;
}

export function taskStateText(task: Task, tone: TaskStateTone): string {
  const m = framesTaskboardMessages;
  if (tone === "question") return translate(m, "waitingForAnswer");
  if (tone === "working") {
    const name = taskAssigneeName(task);
    return name
      ? translate(m, "agentWorking", { name })
      : translate(m, "someAgentWorking");
  }
  const [first, ...rest] = activeBlockers(task);
  if (first) {
    return rest.length > 0
      ? translate(m, "blockedByMore", {
          ticket: first.ticket_display,
          count: rest.length,
        })
      : translate(m, "blockedBy", { ticket: first.ticket_display });
  }
  return translate(
    m,
    task.checks_status === "failure" ? "checksFailed" : "statusBlocked",
  );
}

const DIRECT_SESSION_PROVIDER_NAMES: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex",
  opencode: "OpenCode",
};

export function directSessionProviderName(provider: string): string {
  return DIRECT_SESSION_PROVIDER_NAMES[provider.toLowerCase()] ?? provider;
}

/** OpenCode's link opens the session's folder as a project, where the
 * session is listed; the other apps open the session itself. */
export function directSessionOpenLabel(provider: string): string {
  const name = directSessionProviderName(provider);
  return translate(
    framesTaskboardMessages,
    provider.toLowerCase() === "opencode" ? "openProjectIn" : "openIn",
    { name },
  );
}

export function taskAssigneeName(
  task: Pick<
    Task,
    | "assigned_agent_detail"
    | "assigned_user_detail"
    | "direct_session"
    | "native_subagent"
  >,
): string | null {
  return (
    (task.direct_session
      ? directSessionProviderName(task.direct_session.provider)
      : null) ??
    task.assigned_agent_detail?.name ??
    task.assigned_user_detail?.name ??
    (task.native_subagent
      ? translate(framesTaskboardMessages, "subAgent")
      : null) ??
    null
  );
}

/** The pull request state in plain words: “PR #482”, “Merged”. */
export function taskPullRequestText(
  task: Pick<Task, "ship_phase" | "pr_number" | "pr_url">,
): string | null {
  const m = framesTaskboardMessages;
  if (task.ship_phase === "merged") return translate(m, "merged");
  if (task.pr_number) {
    return translate(m, "pullRequestNumber", { number: task.pr_number });
  }
  return task.pr_url ? translate(m, "pushed") : null;
}

/** Tasks with a pending agent question, sorted to serve as a stable key. */
export function questionTaskIds(
  awaitingInputs: ReadonlyMap<string, AwaitingInput[]>,
): string[] {
  const ids = new Set<string>();
  for (const inputs of awaitingInputs.values()) {
    for (const input of inputs) if (input.taskId) ids.add(input.taskId);
  }
  return [...ids].sort();
}

export function oldestTaskQuestion(
  awaitingInputs: ReadonlyMap<string, AwaitingInput[]>,
  taskId: string,
): AwaitingInput | null {
  let oldest: AwaitingInput | null = null;
  for (const inputs of awaitingInputs.values()) {
    for (const input of inputs) {
      if (
        input.taskId === taskId &&
        (!oldest || input.createdAt < oldest.createdAt)
      ) {
        oldest = input;
      }
    }
  }
  return oldest;
}

/** Tasks an agent runs right now, sorted to serve as a stable key. */
export function runningTaskIds(
  executions: ReadonlyMap<string, ActiveExecution>,
): string[] {
  const ids = new Set<string>();
  for (const execution of executions.values()) {
    if (execution.taskId && LIVE_STATUSES.has(execution.status)) {
      ids.add(execution.taskId);
    }
  }
  return [...ids].sort();
}

/** The task's execution that Stop or Resume acts on: live before paused, newest first. */
export function liveTaskExecution(
  executions: ReadonlyMap<string, ActiveExecution>,
  taskId: string,
): ActiveExecution | undefined {
  return [...executions.values()]
    .filter(
      (execution) =>
        execution.taskId === taskId &&
        (LIVE_STATUSES.has(execution.status) || execution.status === "paused"),
    )
    .sort((a, b) => {
      const pausedDelta =
        Number(a.status === "paused") - Number(b.status === "paused");
      if (pausedDelta !== 0) return pausedDelta;
      return (b.startedAt ?? "").localeCompare(a.startedAt ?? "");
    })[0];
}
